import React, { createContext, useContext, useState, useRef, useEffect } from 'react';
import { apiService } from '../../../services/apiService';

interface SummaryContextValue {
  isAdmin: boolean;
  sessionId: string;
  coachingOverrides: Record<string, string[]>;
}

/**
 * Carries admin status, sessionId, and coaching overrides to all nested
 * SummarySectionCards without prop-drilling through every individual card.
 * Set once at CallSummaryTab level.
 */
export const SummaryAdminContext = createContext<SummaryContextValue>({
  isAdmin: false,
  sessionId: '',
  coachingOverrides: {},
});

export interface SummarySectionCardProps {
  title: string;
  statusBadge?: React.ReactNode;
  resultContent: React.ReactNode;
  coachingItems?: string[];
  coachingContent?: React.ReactNode;
  onViewDetails?: () => void;
  onViewInTimeline?: () => void;
  missingDataMessage?: string;
  className?: string;
  defaultExpanded?: boolean;
  /**
   * Unique key identifying this section (e.g. 'speed', 'tone').
   * When provided, admin edits are persisted to the database.
   */
  sectionKey?: string;
}

const SummarySectionCard: React.FC<SummarySectionCardProps> = ({
  title,
  statusBadge,
  resultContent,
  coachingItems,
  coachingContent,
  onViewDetails,
  onViewInTimeline,
  missingDataMessage,
  className = '',
  defaultExpanded = false,
  sectionKey,
}) => {
  const { isAdmin, sessionId, coachingOverrides } = useContext(SummaryAdminContext);

  const [isBodyExpanded, setIsBodyExpanded] = useState(defaultExpanded);
  const [isCoachingExpanded, setIsCoachingExpanded] = useState(true);
  const [localCoachingItems, setLocalCoachingItems] = useState<string[]>(() => {
    if (sectionKey && coachingOverrides[sectionKey]) return coachingOverrides[sectionKey];
    return coachingItems ?? [];
  });

  // editingIndex: -1 = adding new, >=0 = editing existing, null = idle
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editValue, setEditValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  // Sync when prop or overrides change (e.g. after re-analysis loads)
  useEffect(() => {
    if (sectionKey && coachingOverrides[sectionKey] !== undefined) {
      setLocalCoachingItems(coachingOverrides[sectionKey]);
    } else {
      setLocalCoachingItems(coachingItems ?? []);
    }
  }, [coachingItems, sectionKey, coachingOverrides]);

  useEffect(() => {
    if (editingIndex !== null) inputRef.current?.focus();
  }, [editingIndex]);

  const persistItems = async (items: string[]) => {
    if (!sectionKey || !sessionId) return;
    try {
      await apiService.patch(`/admin/sessions/${sessionId}/call-insights-coaching`, {
        sectionKey,
        items,
      });
    } catch (err) {
      console.warn('[SummarySectionCard] Failed to persist coaching override:', err);
    }
  };

  const startEdit = (idx: number) => {
    setEditValue(localCoachingItems[idx]);
    setEditingIndex(idx);
  };

  const startAdd = () => {
    setEditValue('');
    setEditingIndex(-1);
  };

  const commitEdit = async () => {
    if (editingIndex === null) return;

    let newItems: string[];
    if (editingIndex === -1) {
      newItems = editValue.trim()
        ? [...localCoachingItems, editValue.trim()]
        : [...localCoachingItems];
    } else {
      newItems = editValue.trim()
        ? localCoachingItems.map((item, i) => (i === editingIndex ? editValue.trim() : item))
        : localCoachingItems.filter((_, i) => i !== editingIndex);
    }

    setLocalCoachingItems(newItems);
    setEditingIndex(null);
    await persistItems(newItems);
  };

  const cancelEdit = () => setEditingIndex(null);

  const hasCoaching = coachingContent != null || localCoachingItems.length > 0 || isAdmin;

  return (
    <div
      className={`bg-white border border-hairline rounded-xl shadow-md flex flex-col overflow-hidden ${className}`}
    >
      {/* ── Header: toggles card body ── */}
      <button
        type="button"
        onClick={() => setIsBodyExpanded((v) => !v)}
        className="w-full flex items-center justify-between px-4 py-3 bg-surface-subtle border-b border-hairline hover:bg-surface-sunken transition-colors text-left"
      >
        <div className="flex items-center gap-2.5 min-w-0">
          <span className="text-sm font-bold text-ink uppercase tracking-wide truncate">{title}</span>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0 ml-2">
          {statusBadge}
          <span
            className="text-[10px] text-ink-muted transition-transform duration-200 inline-block"
            style={{ transform: isBodyExpanded ? 'rotate(0deg)' : 'rotate(-90deg)' }}
          >
            &#9660;
          </span>
        </div>
      </button>

      {isBodyExpanded && (
        <>
          {missingDataMessage ? (
            <div className="flex flex-col items-center justify-center p-6 text-center">
              <p className="text-xs text-ink-muted">{missingDataMessage}</p>
            </div>
          ) : (
            <>
              {/* ── Result ── */}
              <div className="px-4 pt-4 pb-3">
                <p className="text-[10px] font-semibold text-ink-muted uppercase tracking-widest mb-2">
                  Result
                </p>
                {resultContent}
              </div>

              {/* ── Coaching ── */}
              {hasCoaching && (
                <div className="px-4 pb-3 border-t border-dashed border-hairline mt-1 pt-3">
                  <button
                    type="button"
                    onClick={() => setIsCoachingExpanded((v) => !v)}
                    className="flex items-center gap-1 w-full text-left mb-2"
                  >
                    <p className="text-[10px] font-semibold text-ink-muted uppercase tracking-widest">
                      Coaching
                    </p>
                    <span
                      className="text-[10px] text-ink-muted transition-transform duration-150 inline-block ml-1"
                      style={{ transform: isCoachingExpanded ? 'rotate(0deg)' : 'rotate(-90deg)' }}
                    >
                      &#9660;
                    </span>
                  </button>

                  {isCoachingExpanded && (
                    <>
                      {coachingContent}
                      {localCoachingItems.length > 0 && (
                        <ul className="space-y-1.5">
                          {localCoachingItems.map((item, idx) => (
                            <li key={idx} className="flex items-start gap-2 text-xs text-ink-secondary">
                              <span className="text-ink-muted font-bold mt-0.5 flex-shrink-0">•</span>
                              {editingIndex === idx ? (
                                <input
                                  ref={inputRef}
                                  type="text"
                                  value={editValue}
                                  onChange={(e) => setEditValue(e.target.value)}
                                  onBlur={commitEdit}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') commitEdit();
                                    else if (e.key === 'Escape') cancelEdit();
                                  }}
                                  className="flex-1 border border-ink-border rounded px-1.5 py-0.5 text-xs focus:outline-none focus:ring-1 focus:ring-signature bg-surface"
                                />
                              ) : (
                                <>
                                  {isAdmin ? (
                                    <button
                                      type="button"
                                      onClick={(e) => { e.stopPropagation(); startEdit(idx); }}
                                      className="flex-1 leading-relaxed text-left hover:text-signature transition-colors cursor-text"
                                      title="Click to edit"
                                    >
                                      {item}
                                    </button>
                                  ) : (
                                    <span className="flex-1 leading-relaxed">{item}</span>
                                  )}
                                  {isAdmin && (
                                    <button
                                      type="button"
                                      onClick={(e) => { e.stopPropagation(); startEdit(idx); }}
                                      className="text-ink-muted hover:text-signature transition-colors flex-shrink-0"
                                      title="Edit coaching point"
                                    >
                                      <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"/>
                                      </svg>
                                    </button>
                                  )}
                                </>
                              )}
                            </li>
                          ))}
                        </ul>
                      )}

                      {/* New-item input */}
                      {isAdmin && editingIndex === -1 && (
                        <input
                          ref={inputRef}
                          type="text"
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                          onBlur={commitEdit}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') commitEdit();
                            else if (e.key === 'Escape') cancelEdit();
                          }}
                          placeholder="Type coaching point, then press Enter..."
                          className="mt-2 w-full border border-ink-border rounded px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-signature bg-surface"
                        />
                      )}

                      {isAdmin && editingIndex === null && (
                        <button
                          type="button"
                          onClick={startAdd}
                          className="mt-2 text-xs text-signature hover:text-signature-strong font-medium flex items-center gap-1 transition-colors"
                        >
                          <span className="font-bold">+</span>
                          <span>Add coaching point</span>
                        </button>
                      )}
                    </>
                  )}
                </div>
              )}
            </>
          )}

          {/* ── Footer actions ── */}
          {(onViewDetails || onViewInTimeline) && (
            <div className="flex items-center gap-2 px-4 py-3 border-t border-hairline bg-surface-subtle mt-auto">
              {onViewDetails && (
                <button
                  type="button"
                  onClick={onViewDetails}
                  className="text-xs text-signature hover:text-signature-strong font-medium underline"
                >
                  View Full Analysis &rarr;
                </button>
              )}
              {onViewInTimeline && (
                <button
                  type="button"
                  onClick={onViewInTimeline}
                  className="text-xs text-ink-muted hover:text-ink-secondary font-medium underline ml-auto"
                >
                  View in Timeline &rarr;
                </button>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default SummarySectionCard;
